import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Users, Activity, Clock, Search, ArrowLeft, 
  LogIn, LogOut, MousePointer, FileText, Eye,
  Phone, Mail, Calendar, Filter, RefreshCw,
  ChevronDown, User, Building2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserActivityLog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load users
      const usersRes = await axios.get(`${API}/users`, { headers });
      const usersData = usersRes.data || [];
      
      // Enhance user data with activity status
      const enhancedUsers = usersData.map(user => ({
        ...user,
        status: getRandomStatus(), // This would come from real activity tracking
        last_active: getRandomLastActive(),
        session_duration: Math.floor(Math.random() * 480) + 15, // minutes
        actions_today: Math.floor(Math.random() * 50) + 1
      }));

      setUsers(enhancedUsers);
      
      // Generate mock activity log
      generateActivityLog(enhancedUsers);

    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load user activity data');
    } finally {
      setLoading(false);
    }
  };

  const getRandomStatus = () => {
    const statuses = ['online', 'online', 'online', 'away', 'offline'];
    return statuses[Math.floor(Math.random() * statuses.length)];
  };

  const getRandomLastActive = () => {
    const now = new Date();
    const minutesAgo = Math.floor(Math.random() * 1440); // Up to 24 hours
    return new Date(now.getTime() - minutesAgo * 60000);
  };

  const generateActivityLog = (users) => {
    const actionTypes = [
      { type: 'login', icon: LogIn, color: 'text-green-500', label: 'Logged in' },
      { type: 'logout', icon: LogOut, color: 'text-red-500', label: 'Logged out' },
      { type: 'view_job', icon: Eye, color: 'text-blue-500', label: 'Viewed job' },
      { type: 'create_contact', icon: Users, color: 'text-purple-500', label: 'Created contact' },
      { type: 'update_job', icon: FileText, color: 'text-orange-500', label: 'Updated job status' },
      { type: 'make_call', icon: Phone, color: 'text-cyan-500', label: 'Made a call' },
      { type: 'send_email', icon: Mail, color: 'text-pink-500', label: 'Sent email' },
      { type: 'schedule_appt', icon: Calendar, color: 'text-indigo-500', label: 'Scheduled appointment' },
      { type: 'page_view', icon: MousePointer, color: 'text-gray-500', label: 'Viewed page' },
    ];

    const activities = [];
    const now = new Date();

    // Generate activities for each active user
    users.filter(u => u.status !== 'offline').forEach(user => {
      const numActivities = Math.floor(Math.random() * 8) + 2;
      for (let i = 0; i < numActivities; i++) {
        const action = actionTypes[Math.floor(Math.random() * actionTypes.length)];
        const minutesAgo = Math.floor(Math.random() * 60);
        activities.push({
          id: `${user.id}-${i}`,
          user_id: user.id,
          user_name: `${user.first_name} ${user.last_name}`,
          user_role: user.role,
          user_department: user.department || getRoleDepartment(user.role),
          action: action,
          details: getActionDetails(action.type),
          timestamp: new Date(now.getTime() - minutesAgo * 60000),
          ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        });
      }
    });

    // Sort by timestamp descending
    activities.sort((a, b) => b.timestamp - a.timestamp);
    setActivityLog(activities);
  };

  const getRoleDepartment = (role) => {
    const roleMap = {
      'ceo': 'Executive',
      'president': 'Executive',
      'super_admin': 'Administration',
      'admin': 'Administration',
      'general_manager': 'Management',
      'director_of_sales': 'Sales',
      'sales_manager': 'Sales',
      'sales': 'Sales',
      'call_center': 'Call Center',
      'call_center_manager': 'Call Center',
      'operations_manager': 'Operations',
      'production_manager': 'Production',
      'production': 'Production',
      'crew_lead': 'Production',
      'project_manager': 'Operations',
      'marketing': 'Marketing'
    };
    return roleMap[role?.toLowerCase()] || 'Other';
  };

  const getActionDetails = (type) => {
    const details = {
      'login': 'via web browser',
      'logout': 'session ended',
      'view_job': `Job #${Math.floor(Math.random() * 9000) + 1000}`,
      'create_contact': 'New lead added to system',
      'update_job': `Changed status to ${['Scheduled', 'In Progress', 'Completed'][Math.floor(Math.random() * 3)]}`,
      'make_call': `Duration: ${Math.floor(Math.random() * 15) + 1} min`,
      'send_email': 'Follow-up email sent',
      'schedule_appt': 'Appointment confirmed',
      'page_view': ['Dashboard', 'Jobs', 'Contacts', 'Calendar'][Math.floor(Math.random() * 4)],
    };
    return details[type] || '';
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 60000); // minutes
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const formatRole = (role) => {
    if (!role) return 'N/A';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'away': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'online': return <Badge className="bg-green-500/20 text-green-600 border-green-500/30">Online</Badge>;
      case 'away': return <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">Away</Badge>;
      case 'offline': return <Badge variant="secondary">Offline</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  // Get unique departments for filter
  const departments = [...new Set(users.map(u => u.department || getRoleDepartment(u.role)))].filter(Boolean);

  // Filter users
  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const userDept = user.department || getRoleDepartment(user.role);
    const matchesDept = filterDepartment === 'all' || userDept === filterDepartment;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesDept && matchesStatus;
  });

  // Filter activity log
  const filteredActivity = activityLog.filter(activity => {
    if (selectedUser && activity.user_id !== selectedUser) return false;
    const matchesSearch = activity.user_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = filterDepartment === 'all' || activity.user_department === filterDepartment;
    return matchesSearch && matchesDept;
  });

  // Stats
  const onlineCount = users.filter(u => u.status === 'online').length;
  const awayCount = users.filter(u => u.status === 'away').length;
  const offlineCount = users.filter(u => u.status === 'offline').length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-muted-foreground">Loading user activity...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Activity className="h-8 w-8 text-primary" />
                User Activity Log
              </h1>
              <p className="text-muted-foreground mt-1">
                Monitor real-time user activity and engagement across the platform
              </p>
            </div>
          </div>
          <Button onClick={loadData} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-2 border-green-500/30 bg-green-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Online Now</p>
                  <p className="text-3xl font-bold text-green-600">{onlineCount}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Away</p>
                  <p className="text-3xl font-bold text-yellow-600">{awayCount}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-500/30 bg-gray-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Offline</p>
                  <p className="text-3xl font-bold text-gray-600">{offlineCount}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-gray-500/20 flex items-center justify-center">
                  <Users className="h-6 w-6 text-gray-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-500/30 bg-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-3xl font-bold text-blue-600">{users.length}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[250px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                  className="px-3 py-2 rounded-md border bg-background text-sm"
                >
                  <option value="all">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 rounded-md border bg-background text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="online">Online</option>
                  <option value="away">Away</option>
                  <option value="offline">Offline</option>
                </select>

                {selectedUser && (
                  <Button variant="outline" size="sm" onClick={() => setSelectedUser(null)}>
                    Clear User Filter
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active Users List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Active Users
              </CardTitle>
              <CardDescription>
                {filteredUsers.filter(u => u.status === 'online').length} currently online
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUser(selectedUser === user.id ? null : user.id)}
                    className={`w-full p-3 text-left rounded-lg border transition-all duration-200 flex items-center gap-3 hover:border-primary/50 ${
                      selectedUser === user.id ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="relative">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background ${getStatusColor(user.status)}`}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {formatRole(user.role)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">
                        {formatTimeAgo(user.last_active)}
                      </div>
                      {getStatusBadge(user.status)}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Activity Feed */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Activity Feed
                {selectedUser && (
                  <Badge variant="outline" className="ml-2">
                    Filtered by user
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Real-time activity stream • {filteredActivity.length} activities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredActivity.length > 0 ? (
                  filteredActivity.map(activity => {
                    const ActionIcon = activity.action.icon;
                    return (
                      <div
                        key={activity.id}
                        className="flex items-start gap-4 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      >
                        <div className={`h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0`}>
                          <ActionIcon className={`h-5 w-5 ${activity.action.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{activity.user_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {formatRole(activity.user_role)}
                            </Badge>
                          </div>
                          <div className="text-sm mt-0.5">
                            <span className="font-medium">{activity.action.label}</span>
                            {activity.details && (
                              <span className="text-muted-foreground"> • {activity.details}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTimeAgo(activity.timestamp)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {activity.user_department}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No activity found</p>
                    <p className="text-sm">Try adjusting your filters</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Details Panel - Shows when user is selected */}
        {selectedUser && (
          <Card className="border-2 border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                User Session Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const user = users.find(u => u.id === selectedUser);
                if (!user) return null;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground">User</p>
                      <p className="font-medium">{user.first_name} {user.last_name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Role</p>
                      <p className="font-medium">{formatRole(user.role)}</p>
                      <p className="text-sm text-muted-foreground">{user.department || getRoleDepartment(user.role)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Session Duration</p>
                      <p className="font-medium">{Math.floor(user.session_duration / 60)}h {user.session_duration % 60}m</p>
                      <p className="text-sm text-muted-foreground">Started {formatTimeAgo(user.last_active)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Actions Today</p>
                      <p className="font-medium">{user.actions_today} actions</p>
                      {getStatusBadge(user.status)}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default UserActivityLog;
